import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";

const STORAGE_KEY = "seointent_cookie_consent";
const CURRENT_VERSION = 1;

const DEFAULT_CONSENT = {
  version: CURRENT_VERSION,
  necessary: true,
  analytics: false,
  marketing: false,
  ts: null,
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, version: CURRENT_VERSION, ts: Date.now() })
    );
  } catch {}
}

function applyConsent(consent) {
  if (typeof window === "undefined") return;
  // Tell GA4 and any other tag manager whether to set storage / send hits.
  // GA4 reads window.dataLayer pushes with the 'consent' command.
  try {
    window.dataLayer = window.dataLayer || [];
    const gtag = function () {
      window.dataLayer.push(arguments);
    };
    gtag("consent", "update", {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.analytics ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });
  } catch {}

  // Surface a snapshot other code (PostHog loader, pixel loaders) can read.
  try {
    window.__seointentConsent = { ...consent };
    window.dispatchEvent(new CustomEvent("seointent:consent-changed", { detail: consent }));
  } catch {}

  // If user revoked analytics, clear known GA4 cookies on this domain.
  if (!consent.analytics) {
    try {
      const host = window.location.hostname;
      const parts = host.split(".");
      const root = parts.length >= 2 ? "." + parts.slice(-2).join(".") : host;
      const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
      ["_ga", "_gid", "_gat"].forEach((name) => {
        document.cookie = `${name}=; expires=${expired}; path=/; domain=${root}`;
        document.cookie = `${name}=; expires=${expired}; path=/`;
      });
      Object.keys(document.cookie.split("; ").reduce((acc, c) => {
        const [k] = c.split("=");
        if (k && (k.startsWith("_ga_") || k.startsWith("_gac_"))) acc[k] = true;
        return acc;
      }, {})).forEach((name) => {
        document.cookie = `${name}=; expires=${expired}; path=/; domain=${root}`;
        document.cookie = `${name}=; expires=${expired}; path=/`;
      });
    } catch {}
  }
}

export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
      applyConsent(existing);
    } else {
      // Default-deny until the user chooses.
      applyConsent(DEFAULT_CONSENT);
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const existing = loadConsent();
      if (existing) {
        setAnalytics(!!existing.analytics);
        setMarketing(!!existing.marketing);
      }
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener("seointent:open-cookie-prefs", handler);
    return () => window.removeEventListener("seointent:open-cookie-prefs", handler);
  }, []);

  const persist = useCallback((next) => {
    saveConsent(next);
    applyConsent(next);
    setOpen(false);
    setShowPrefs(false);
  }, []);

  const acceptAll = () => persist({ ...DEFAULT_CONSENT, analytics: true, marketing: true });
  const rejectAll = () => persist({ ...DEFAULT_CONSENT, analytics: false, marketing: false });
  const saveChoices = () => persist({ ...DEFAULT_CONSENT, analytics, marketing });

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
    >
      <div className="mx-auto max-w-3xl bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-[#FF2F2F]" aria-hidden="true" />
              <h2
                id="cookie-banner-title"
                className="font-cabinet font-bold text-lg text-zinc-900"
              >
                We value your privacy
              </h2>
            </div>
            <button
              onClick={() => persist({ ...DEFAULT_CONSENT })}
              aria-label="Close — reject non-essential cookies"
              className="text-zinc-400 hover:text-zinc-900 transition-colors"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p
            id="cookie-banner-desc"
            className="font-satoshi text-sm text-zinc-600 leading-relaxed mb-4"
          >
            We use cookies to keep you logged in (strictly necessary) and — only with your consent —
            to measure how the site is used so we can improve it. Read our{" "}
            <Link to="/cookie-policy" className="text-[#FF2F2F] underline font-semibold">
              Cookie Policy
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="text-[#FF2F2F] underline font-semibold">
              Privacy Policy
            </Link>
            .
          </p>

          {showPrefs && (
            <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-200 mb-4">
              <PrefRow
                label="Strictly necessary"
                description="Required to log in, hold your session, and remember your consent. Cannot be turned off."
                checked
                disabled
                onChange={() => {}}
              />
              <PrefRow
                label="Analytics"
                description="Anonymised usage (GA4, PostHog) so we can improve the product. IPs are anonymised."
                checked={analytics}
                onChange={setAnalytics}
              />
              <PrefRow
                label="Marketing"
                description="Affiliate attribution and (rarely) conversion pixels for ads. Off by default."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
            {!showPrefs && (
              <button
                onClick={() => setShowPrefs(true)}
                type="button"
                className="font-satoshi text-sm font-semibold text-zinc-700 hover:text-zinc-900 px-4 py-2.5 rounded-lg border border-zinc-200 hover:border-zinc-400 transition-colors order-3 sm:order-1"
              >
                Customise
              </button>
            )}
            <button
              onClick={rejectAll}
              type="button"
              className="font-satoshi text-sm font-semibold text-zinc-700 hover:text-zinc-900 px-4 py-2.5 rounded-lg border border-zinc-200 hover:border-zinc-400 transition-colors order-2"
            >
              Reject non-essential
            </button>
            {showPrefs ? (
              <button
                onClick={saveChoices}
                type="button"
                className="font-satoshi text-sm font-bold bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg transition-colors order-1 sm:order-3"
              >
                Save choices
              </button>
            ) : (
              <button
                onClick={acceptAll}
                type="button"
                className="font-satoshi text-sm font-bold bg-[#FF2F2F] hover:bg-[#e02828] text-white px-5 py-2.5 rounded-lg transition-colors order-1 sm:order-3"
                data-testid="cookie-accept-all"
              >
                Accept all
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, description, checked, disabled, onChange }) {
  return (
    <label
      className={`flex items-start justify-between gap-4 p-4 ${
        disabled ? "bg-zinc-50" : "cursor-pointer hover:bg-zinc-50"
      }`}
    >
      <div className="flex-1">
        <p className="font-cabinet font-bold text-sm text-zinc-900">{label}</p>
        <p className="font-satoshi text-xs text-zinc-500 leading-relaxed mt-0.5">
          {description}
        </p>
      </div>
      <div className="pt-0.5">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-[#FF2F2F] cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
    </label>
  );
}
